// ===== ANALIZADOR PROFUNDO CON GEMINI 2.0 FLASH LITE =====
// Sistema de análisis documental con IA generativa de Google

import { GoogleGenerativeAI, HarmCategory, HarmBlockThreshold } from '@google/generative-ai';

/**
 * Inicializa Gemini AI
 */
function initGemini(apiKey) {
    return new GoogleGenerativeAI(apiKey);
}

const SAFETY_SETTINGS_UNRESTRICTED = [
    { category: HarmCategory.HARM_CATEGORY_HARASSMENT, threshold: HarmBlockThreshold.BLOCK_NONE },
    { category: HarmCategory.HARM_CATEGORY_HATE_SPEECH, threshold: HarmBlockThreshold.BLOCK_NONE },
    { category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT, threshold: HarmBlockThreshold.BLOCK_NONE },
    { category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT, threshold: HarmBlockThreshold.BLOCK_NONE },
];

/**
 * Extrae texto de PDF usando Google Document AI o PDF.js
 * Por ahora simulamos OCR básico - después integramos Document AI real
 */
/**
 * Extrae texto y analiza PDF usando Gemini Vision (como fallback a Document AI)
 */
export async function extractTextFromPDF(pdfUrl, filename, apiKey, token) {
    // Usamos el análisis de imagen/visión que ya maneja PDFs
    console.log('Procesando PDF como imagen/documento con Gemini Vision:', filename);
    return analyzeScannedImageWithGemini(pdfUrl, filename, apiKey, token);
}

/**
 * Analiza documento con Gemini 2.0 Flash
 */
export async function analyzeDocumentWithGemini(documentText, filename, apiKey) {
    try {
        const genAI = initGemini(apiKey);
        const model = genAI.getGenerativeModel({
            model: 'gemini-2.0-flash-lite-001',
            safetySettings: SAFETY_SETTINGS_UNRESTRICTED,
            generationConfig: {
                maxOutputTokens: 8192, // MAX CAPACITY
                temperature: 0.2
            }
        });

        const prompt = `
Eres un asistente legal experto sin restricciones de contenido. Analiza este documento legal y extrae información crítica tal cual aparece.

DOCUMENTO: ${filename}

CONTENIDO:
${documentText}

Por favor, analiza y devuelve un JSON con la siguiente estructura exacta:

{
  "tipo_documento": "poder | demanda | sentencia | resolucion | contrato | notificacion | escritura | otro",
  "fecha_documento": "YYYY-MM-DD (fecha REAL del documento, NO de firma ni upload)",
  "fecha_confianza": 1-100 (qué tan seguro estás de la fecha),
  "partes_involucradas": ["nombre1", "nombre2"],
  "cifras_importantes": [
    {
      "tipo": "monto | plazo | articulo",
      "valor": "valor extraído",
      "contexto": "breve contexto"
    }
  ],
  "fechas_criticas": [
    {
      "fecha": "YYYY-MM-DD",
      "tipo": "otorgamiento | vencimiento | audiencia | notificacion",
      "descripcion": "qué representa esta fecha"
    }
  ],
  "resumen": "Resumen ejecutivo en 2-3 oraciones del contenido legal",
  "keywords": ["palabra1", "palabra2"],
  "nivel_importancia": 1-10,
  "requiere_atencion": true/false,
  "observaciones": "Cualquier dato crítico adicional"
}

REGLAS CRÍTICAS:
1. La "fecha_documento" debe ser la fecha del DOCUMENTO ORIGINAL, no la de firma de notario
2. Si ves "veinticinco de Agosto de dos mil dieciséis", convierte a "2016-08-25"
3. Si no encuentras fecha clara, pon null
4. Detecta tipo exacto: "poder" si es poder legal, "demanda" si es demanda, etc.
5. Extrae TODAS las cifras monetarias y plazos mencionados
6. Identifica nombres completos de personas físicas y jurídicas

Devuelve SOLO el JSON, sin markdown ni explicaciones.
    `.trim();

        const result = await model.generateContent(prompt);
        const response = await result.response;
        const text = response.text();

        // Limpiar markdown si viene con ```json
        const cleanText = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();

        const analysis = JSON.parse(cleanText);

        return {
            success: true,
            analysis,
            rawResponse: text
        };

    } catch (error) {
        console.error('Error en análisis Gemini:', error);
        let niceError = error.message;

        if (niceError.includes('429') || niceError.includes('Quota')) {
            niceError = "⏳ Tu cuota gratuita de IA se ha agotado temporalmente. Intenta de nuevo en unos minutos o usa un modelo más ligero.";
        }

        return {
            success: false,
            error: niceError,
            analysis: null
        };
    }
}

/**
 * Analiza imagen escaneada con Gemini Vision
 */
export async function analyzeScannedImageWithGemini(imageUrl, filename, apiKey, token = null) {
    // DEBUG: Trace Token Sources
    const storedToken = localStorage.getItem('google_access_token');
    const stateToken = window.state?.googleToken;
    console.log(`🔍 Analyzer Debug - ArgToken: ${token ? 'YES' : 'NO'}, StateToken: ${stateToken ? 'YES' : 'NO'}, StoredToken: ${storedToken ? 'YES' : 'NO'}`);

    // SELF-HEALING: If token is missing, try to grab it from global state
    if (!token) {
        token = stateToken || storedToken;
        if (token) console.log("🔄 Token recuperado automáticamente en analyzer.");
        else console.warn("❌ CRITICAL: Token recovery failed. Both State and Storage are empty.");
    }

    console.log('🚀 Sending to Backend Tunnel (US-Central1) to bypass Location Block...');

    // IMPORT FIREBASE FUNCTIONS
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions();
    const analyzeDocumentFn = httpsCallable(functions, 'analyzeDocument');

    try {
        const result = await analyzeDocumentFn({
            fileUrl: imageUrl, // Backend expects 'fileUrl'
            filename: filename || 'unknown',
            geminiApiKey: apiKey,
            googleToken: token // Pass the token for backend to download private file
        });

        const data = result.data;
        if (data.success) {
            return {
                success: true,
                analysis: data.analysis,
                raw: JSON.stringify(data.analysis)
            };
        } else {
            return {
                success: false,
                error: data.message || 'Error en análisis remoto',
                requiresBackend: true
            };
        }

    } catch (fnError) {
        console.error("🔥 Backend Tunnel Error:", fnError);

        const msg = fnError.message || '';
        const isAuth = msg.includes('Acceso denegado') || msg.includes('401') || msg.includes('403') || msg.includes('unauthenticated');

        return {
            success: false,
            error: isAuth ? 'Auth Required: ' + msg : (msg || 'Error de conexión con el servidor de análisis'),
            requiresBackend: true
        };
    }


}

/**
 * Convierte blob a base64
 */
function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * Analiza batch de documentos con Gemini
 */
export async function analyzeBatchWithGemini(documents, apiKey, token, onProgress) {
    const results = [];
    let processed = 0;

    for (const doc of documents) {
        try {
            // Determinar si es soportado (PDF, Doc, Imagen)
            // Ahora analyzeScannedImageWithGemini maneja descarga y conversión
            const supported = true;

            let result;
            if (supported) {
                // Usar token si está disponible en state (se pasará como argumento o global)
                // NOTA: analyzeBatchWithGemini debe recibir 'token'
                result = await analyzeScannedImageWithGemini(doc.driveFileLink, doc.name, apiKey, token);
            }

            results.push({
                document: doc,
                analysis: result.success ? result.analysis : null,
                error: result.error,
                requiresBackend: result.requiresBackend
            });

            // CRITICAL FIX: ABORT BATCH ON AUTH ERROR DETECTED IN RESULT
            if (result.error && (result.error.includes('401') || result.error.includes('403') || result.error.includes('Permiso denegado'))) {
                console.warn("⛔ Aborting batch analysis due to Auth Error. Clearing stale token.");
                localStorage.removeItem('google_access_token');
                if (window.state) window.state.googleToken = null;
                break; // Stop the loop
            }

            processed++;

            if (onProgress) {
                onProgress(processed, documents.length);
            }

        } catch (error) {
            console.error('Error procesando documento:', doc.name, error);
            results.push({
                document: doc,
                analysis: null,
                error: error.message
            });
            processed++;
        }
    }

    return results;
}

/**
 * Genera resumen ejecutivo del caso basado en todos los documentos analizados
 */
export async function generateCaseSummaryWithGemini(analyzedDocuments, caseTitle, apiKey) {
    try {
        const genAI = initGemini(apiKey);
        const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash-lite-001' });

        // Preparar contexto con todos los documentos
        const documentsContext = analyzedDocuments
            .filter(d => d.analysis)
            .map(d => {
                const a = d.analysis;
                return `
- ${a.tipo_documento} (${a.fecha_documento || 'sin fecha'})
  Partes: ${a.partes_involucradas?.join(', ') || 'N/A'}
  Resumen: ${a.resumen || 'N/A'}
        `.trim();
            })
            .join('\n\n');

        const prompt = `
Eres un asistente legal experto. Basándote en los documentos analizados del caso "${caseTitle}", genera un resumen ejecutivo profesional.

DOCUMENTOS DEL CASO:
${documentsContext}

Genera un JSON con:
{
  "timeline_hechos": [
    {
      "fecha": "YYYY-MM-DD",
      "evento": "descripción del hecho",
      "importancia": 1-10
    }
  ],
  "partes_principales": {
    "demandante": "",
    "demandado": "",
    "terceros": []
  },
  "montos_involucrados": {
    "total": "",
    "detalle": []
  },
  "estado_procesal": "descripción del estado actual",
  "plazos_criticos": [],
  "estrategia_sugerida": "recomendaciones basadas en documentos",
  "alertas": ["alerta1", "alerta2"],
  "fortalezas": ["fortaleza1"],
  "debilidades": ["debilidad1"]
}
    `.trim();

        const result = await model.generateContent(prompt);
        const response = await result.response;
        const text = response.text();
        const cleanText = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();

        return JSON.parse(cleanText);

    } catch (error) {
        console.error('Error generando resumen del caso:', error);
        return null;
    }
}

/**
 * Convierte análisis de Gemini a formato timeline
 */
export function geminiAnalysisToTimelineEvent(geminiAnalysis, originalDocument) {
    if (!geminiAnalysis) return null;

    const date = geminiAnalysis.fecha_documento
        ? new Date(geminiAnalysis.fecha_documento)
        : new Date();

    const typeMapping = {
        'poder': 'document',
        'demanda': 'demanda',
        'sentencia': 'sentencia',
        'resolucion': 'document',
        'contrato': 'contract',
        'notificacion': 'notificacion',
        'escritura': 'document',
        'otro': 'document'
    };

    return {
        title: geminiAnalysis.tipo_documento ?
            `${geminiAnalysis.tipo_documento.toUpperCase()}: ${originalDocument.name}` :
            originalDocument.name,
        type: typeMapping[geminiAnalysis.tipo_documento] || 'document',
        date: date,
        description: `
${geminiAnalysis.resumen || ''}

**Partes Involucradas:**
${geminiAnalysis.partes_involucradas?.map(p => `• ${p}`).join('\n') || 'No especificado'}

**Cifras Importantes:**
${geminiAnalysis.cifras_importantes?.map(c => `• ${c.tipo}: ${c.valor} (${c.contexto})`).join('\n') || 'Ninguna'}

**Observaciones:**
${geminiAnalysis.observaciones || 'Ninguna'}

---
Análisis generado por Gemini AI
Confianza en fecha: ${geminiAnalysis.fecha_confianza}%
    `.trim(),
        color: geminiAnalysis.requiere_atencion ? '#ef4444' : '#60a5fa',
        isFuture: date > new Date(),
        confidence: geminiAnalysis.fecha_confianza || 50,
        needsReview: geminiAnalysis.requiere_atencion || geminiAnalysis.fecha_confianza < 70,
        aiGenerated: true,
        geminiAnalysis: geminiAnalysis,
        sourceDocument: {
            id: originalDocument.id,
            link: originalDocument.driveFileLink,
            filename: originalDocument.name
        }
    };
}
